import { useEffect, useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react'
import {
  useCreateApiKeyMutation,
  type EntitiesCreateUserSessionResponse,
  type EntitiesUserResponse
} from '@axilon-apps/domains-identity/state'

import { Button } from '../../../../components/ui/button'
import { Checkbox } from '../../../../components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../../../components/ui/dialog'
import { Input } from '../../../../components/ui/input'
import { Label } from '../../../../components/ui/label'
import { RadioGroup, RadioGroupItem } from '../../../../components/ui/radio-group'
import { apiKeyErrorMessage } from './api-key-error'
import {
  API_KEY_PERMISSION,
  API_KEY_PERMISSION_OPTIONS,
  apiKeyPermissionLabel,
  type ApiKeyPermission
} from './api-key-permissions'
import { apiKeyExpiryValue, type ApiKeyExpiryChoice } from './expiry'

interface CreateApiKeyDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onIssued: (key: EntitiesUserResponse, token: EntitiesCreateUserSessionResponse) => void
}

const STEP_LABELS = ['Name', 'Permissions', 'Expiry', 'Review'] as const

export function CreateApiKeyDialog({ open, onOpenChange, onIssued }: CreateApiKeyDialogProps) {
  const [step, setStep] = useState(0)
  const [name, setName] = useState('')
  const [permissions, setPermissions] = useState<ApiKeyPermission[]>([
    API_KEY_PERMISSION.IngestPush
  ])
  const [expiryChoice, setExpiryChoice] = useState<ApiKeyExpiryChoice>('1-year')
  const [customDate, setCustomDate] = useState('')
  const [error, setError] = useState('')
  const [createApiKey, createState] = useCreateApiKeyMutation()

  useEffect(() => {
    if (!open) return
    setStep(0)
    setName('')
    setPermissions([API_KEY_PERMISSION.IngestPush])
    setExpiryChoice('1-year')
    setCustomDate('')
    setError('')
  }, [open])

  const expiry = useMemo(
    () => apiKeyExpiryValue(expiryChoice, customDate),
    [customDate, expiryChoice]
  )

  const validateStep = (): string => {
    if (step === 0) {
      const trimmed = name.trim()
      if (!trimmed) return 'Name is required.'
      if ([...trimmed].length > 64) return 'Name must be 64 characters or fewer.'
    }
    if (step === 1 && permissions.length === 0) return 'Choose at least one permission.'
    if (step === 2 && expiry.error) return expiry.error
    return ''
  }

  const next = () => {
    const validationError = validateStep()
    setError(validationError)
    if (!validationError) setStep((current) => Math.min(current + 1, STEP_LABELS.length - 1))
  }

  const submit = async () => {
    if (permissions.length === 0) {
      setError('Choose at least one permission.')
      return
    }
    const submissionExpiry = apiKeyExpiryValue(expiryChoice, customDate)
    if (!submissionExpiry.expiresInSeconds) {
      setError(submissionExpiry.error || 'Choose a valid expiry.')
      return
    }
    setError('')
    try {
      const result = await createApiKey({
        name: name.trim(),
        permissions,
        expiresInSeconds: submissionExpiry.expiresInSeconds
      }).unwrap()
      createState.reset()
      onOpenChange(false)
      onIssued(result.user, result.token)
    } catch (requestError) {
      setError(apiKeyErrorMessage(requestError))
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!createState.isLoading) onOpenChange(nextOpen)
      }}
    >
      <DialogContent className="max-h-[calc(100vh-2rem)] min-w-0 overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>New API key</DialogTitle>
          <DialogDescription>
            Step {step + 1} of {STEP_LABELS.length}: {STEP_LABELS[step]}
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-1" aria-label="API key creation progress">
          {STEP_LABELS.map((label, index) => (
            <div
              key={label}
              className={`h-1 flex-1 rounded-full ${index <= step ? 'bg-brand' : 'bg-bg-sunk'}`}
            />
          ))}
        </div>

        {step === 0 ? (
          <div className="grid gap-2">
            <Label htmlFor="api-key-name">Name</Label>
            <Input
              id="api-key-name"
              value={name}
              maxLength={64}
              autoFocus
              placeholder="Ignition gateway — 311D"
              onChange={(event) => {
                setName(event.target.value)
                setError('')
              }}
            />
            <p className="text-xs text-ink-3">
              Use the system that will store it, for example “Ignition gateway — 311D”.
            </p>
          </div>
        ) : null}

        {step === 1 ? (
          <div className="grid gap-3">
            <div>
              <Label>Permissions</Label>
              <p className="mt-1 text-xs text-ink-3">Choose what this key is allowed to do.</p>
            </div>
            <div className="grid gap-2">
              {API_KEY_PERMISSION_OPTIONS.map((option) => {
                const checked = permissions.includes(option.value)
                return (
                  <Label
                    key={option.value}
                    className="flex cursor-pointer items-start gap-3 rounded-md border border-border p-3"
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(nextChecked) => {
                        setPermissions((current) =>
                          nextChecked === true
                            ? [...new Set([...current, option.value])]
                            : current.filter((permission) => permission !== option.value)
                        )
                        setError('')
                      }}
                    />
                    <span>
                      <span className="block text-sm font-medium">{option.label}</span>
                      <span className="block text-xs font-normal text-ink-3">
                        {option.description}
                      </span>
                    </span>
                  </Label>
                )
              })}
            </div>
          </div>
        ) : null}

        {step === 2 ? (
          <div className="grid gap-3">
            <Label>Expiry</Label>
            <RadioGroup
              value={expiryChoice}
              onValueChange={(value) => {
                setExpiryChoice(value as ApiKeyExpiryChoice)
                setError('')
              }}
              className="grid gap-2 sm:grid-cols-3"
            >
              <Label className="flex cursor-pointer items-center gap-2 rounded-md border border-border p-3">
                <RadioGroupItem value="90-days" />
                90 days
              </Label>
              <Label className="flex cursor-pointer items-center gap-2 rounded-md border border-border p-3">
                <RadioGroupItem value="1-year" />1 year
              </Label>
              <Label className="flex cursor-pointer items-center gap-2 rounded-md border border-border p-3">
                <RadioGroupItem value="custom" />
                Custom
              </Label>
            </RadioGroup>
            {expiryChoice === 'custom' ? (
              <Input
                aria-label="Custom expiry date"
                type="date"
                value={customDate}
                onChange={(event) => {
                  setCustomDate(event.target.value)
                  setError('')
                }}
              />
            ) : null}
            {expiry.expiresAt && !expiry.error ? (
              <p className="text-xs text-ink-3">Expires {expiry.expiresAt.toLocaleString()}.</p>
            ) : null}
          </div>
        ) : null}

        {step === 3 ? (
          <dl className="grid grid-cols-[7rem_1fr] gap-x-3 gap-y-2 rounded-md border border-border bg-bg-sunk p-3 text-sm">
            <dt className="text-ink-3">Name</dt>
            <dd className="font-medium text-ink">{name.trim()}</dd>
            <dt className="text-ink-3">Permissions</dt>
            <dd className="text-ink">{permissions.map(apiKeyPermissionLabel).join(', ')}</dd>
            <dt className="text-ink-3">Expires</dt>
            <dd className="text-ink">{expiry.expiresAt?.toLocaleString()}</dd>
          </dl>
        ) : null}

        {error ? (
          <div role="alert" className="rounded-md bg-st-down-bg px-3 py-2 text-xs text-st-down">
            {error}
          </div>
        ) : null}

        <DialogFooter>
          {step > 0 ? (
            <Button
              type="button"
              variant="outline"
              disabled={createState.isLoading}
              onClick={() => {
                setError('')
                setStep((current) => current - 1)
              }}
            >
              <ChevronLeft /> Back
            </Button>
          ) : null}
          {step < STEP_LABELS.length - 1 ? (
            <Button type="button" onClick={next}>
              Next <ChevronRight />
            </Button>
          ) : (
            <Button type="button" disabled={createState.isLoading} onClick={() => void submit()}>
              {createState.isLoading ? <Loader2 className="animate-spin" /> : null}
              Create key
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
